import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Star, Clock, Home, MapPin, MessageSquare, Calendar } from "lucide-react";

export default function TenantService() {
  const { slug, serviceId } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    api.get(`/public/tenant/${slug}/services/${serviceId}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug, serviceId]);

  if (missing) {
    return (
      <div className="p-8 text-center" data-testid="tenant-service-missing">
        <div className="font-bold">This service is not available.</div>
        <Link to={`/t/${slug}`} className="inline-block mt-4 brand-primary px-6 py-3 rounded-full text-sm font-semibold">Browse services</Link>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-sm brand-text-muted">Loading…</div>;
  const { service, sellers, reviews } = data;
  const price = service.discount_price || service.price;

  return (
    <div data-testid="tenant-service-page" className="md:grid md:grid-cols-2 md:gap-8 md:px-6 md:py-8 md:items-start">
      <div className="aspect-[16/10] brand-muted md:rounded-2xl md:overflow-hidden md:sticky md:top-24">
        {service.images?.[0] && <img src={service.images[0]} alt="" className="h-full w-full object-cover" />}
      </div>

      <div className="min-w-0">
        <div className="px-5 py-5 md:p-0">
          <div className="text-xs brand-text-muted flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Star className="h-3 w-3 fill-current" style={{ color: "#FBBF24" }} /> {service.rating?.toFixed(1) || "4.5"} ({service.total_reviews})</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {service.duration_minutes} min</span>
            {service.home_service && <span className="flex items-center gap-1"><Home className="h-3 w-3" /> Home service</span>}
            {service.center_visit && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> Visit center</span>}
          </div>
          <h1 className="brand-heading text-2xl md:text-3xl font-bold mt-2 leading-tight break-words">{service.name}</h1>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-2xl font-bold">{formatINR(price)}</div>
            {service.discount_price && <div className="text-sm line-through brand-text-muted">{formatINR(service.price)}</div>}
          </div>
          <p className="mt-4 text-sm brand-text-muted leading-relaxed break-words">{service.description}</p>

          {service.addons?.length > 0 && (
            <div className="mt-6">
              <div className="brand-heading font-bold text-sm uppercase tracking-wider brand-text-muted">Add-ons</div>
              <div className="mt-2 space-y-2">
                {service.addons.map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 brand-muted rounded-xl px-4 py-3 text-sm">
                    <span className="min-w-0 break-words">{a.name}</span><span className="font-bold flex-shrink-0">+{formatINR(a.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sellers?.length > 0 && (
            <div className="mt-6">
              <div className="brand-heading font-bold text-sm uppercase tracking-wider brand-text-muted">Trusted by</div>
              <div className="scroll-rail mt-2 -mx-5 md:mx-0 md:px-0">
                {sellers.map((s) => (
                  <div key={s.id} className="brand-card w-40 p-3 text-center">
                    <div className="h-14 w-14 rounded-full overflow-hidden mx-auto brand-muted">
                      {s.avatar_url && <img src={s.avatar_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="text-xs font-bold mt-2 leading-tight break-words">{s.business_name}</div>
                    <div className="text-[10px] brand-text-muted mt-0.5 flex items-center justify-center gap-1">
                      <Star className="h-2.5 w-2.5 fill-current" style={{ color: "#FBBF24" }} /> {s.rating?.toFixed(1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {service.faqs?.length > 0 && (
            <div className="mt-6">
              <div className="brand-heading font-bold text-sm uppercase tracking-wider brand-text-muted">FAQs</div>
              <div className="mt-2 space-y-2">
                {service.faqs.map((f, i) => (
                  <div key={i} className="brand-card p-4">
                    <div className="text-sm font-semibold">{f.q}</div>
                    <div className="text-xs brand-text-muted mt-1">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {reviews?.length > 0 && (
            <div className="mt-6">
              <div className="brand-heading font-bold text-sm uppercase tracking-wider brand-text-muted">Reviews</div>
              <div className="mt-2 space-y-2">
                {reviews.map((r) => (
                  <div key={r.id} className="brand-card p-4">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold">{r.customer_name}</span>
                      <span className="brand-text-muted">· {r.rating}★</span>
                    </div>
                    <div className="text-sm mt-1 brand-text-muted break-words">{r.comment}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stays above the tab bar on phones; part of the page from 768px */}
        <div className="sticky-cta md:mt-6">
          <div className="brand-surface border-t brand-border md:border md:rounded-2xl px-5 py-3 flex gap-3 items-center">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] brand-text-muted uppercase tracking-wider">Starting at</div>
              <div className="text-lg font-bold leading-none mt-0.5">{formatINR(price)}</div>
            </div>
            {service.requires_enquiry ? (
              <Link to={`/t/${slug}/enquiry?service_id=${service.id}`} className="brand-primary px-6 py-3 rounded-full font-semibold text-sm flex items-center gap-1.5 flex-shrink-0" data-testid="tenant-service-enquiry-btn">
                <MessageSquare className="h-4 w-4" /> Send Enquiry
              </Link>
            ) : (
              <Link to={`/t/${slug}/book/${service.id}`} className="brand-primary px-6 py-3 rounded-full font-semibold text-sm flex items-center gap-1.5 flex-shrink-0" data-testid="tenant-service-book-btn">
                <Calendar className="h-4 w-4" /> Book Now
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
